/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, when, computed, runInAction } from "mobx";
import {
  docViewModes,
  loadStatus,
  httpErrorCodes,
  responseSource,
  pageViewModifiers,
  appIdentifiers,
  hudUiControlAlignments,
  EVENT_NAMES,
  cacheBurstLogKeys,
  AnnotType,
  pageUsageStatus
} from "constants/enums";
import { getViewMode } from "selectors/viewModeSelectors";
import { Page } from "stores/Page";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { getQueryParameterFromUrl, createURLFromBuffer } from "dc-preview-utils/src/Utils/UrlUtils";
import { getImageDimensionsFromBlobUrl, isValidUrl } from "utils/UrlUtils";
import { isInitialPageLoaded, getInitialPageIndex, getIsInitialPageRendered } from "selectors/pagedViewState";
import { isUndefined, isBoolean, noop, isFunction } from "utils/lodashUtils";
import {
  recordFirstPageLoaded,
  recordFirstPageReloadTime,
  recordPageLoadStartTime,
  recordPageLoadEndTime,
  recordPageRenderTime,
  recordProgressivePageRenderTime,
  recordExternalPageLoadEndTime,
  recordExternalPageLoadStartTime,
  recordProgressiveRenderingFirstResponseTimeForPage,
  getFirstPageLoadTime,
  getTimingsToLogWithFirstPageLoad,
  recordFirstPageProgressiveAdditionalTimes
} from "utils/performance";
import { delayedRejectablePromise, allSettled } from "utils/PromiseUtils";
import { initiatePerfLogging } from "logging/performance";
import { logFirstPageLoaded, logFirstRenditionTypeVisible, logResourceMarkers } from "logging";
import {
  PAGE_PRIORITY,
  THUMBNAIL_PRIORITY,
  CACHE_PARTIAL_LOADING_TIME_DELTA,
  ENABLE_TILING_DELTA,
  DEFAULT_PAGE_HEIGHT,
  DEFAULT_PAGE_WIDTH,
  ACCESSIBILITY_CACHE_PRIORITY,
  YOUTUBE_LINKS_CNT_PAGES_THRESHOLD,
  GET_HYPERLINK_LIST_FOR_CNT_PRIORITY,
  YOUTUBE_REG_PATTERN,
  OVERLAY_VISIBILITY,
  FILL_SIGN_OCG_NAME,
  PAGES_VIEW_MARGIN_LEFT,
  PAGES_VIEW_MARGIN_RIGHT,
  PROGRESSIVE_RENDERING_INTERVAL,
  PROGRESSIVE_RENDERING_INTERVAL_FIREFOX,
  PROGRESSIVE_RENDERING_INTERVAL_LIMIT,
  PROGRESSIVE_RENDERING_MULTIPLIER,
  PROGRESSIVE_RENDERING_WORKFLOWS,
  MAX_LOG_FOR_PER_PAGE_RENDER_TIME,
  FORCE_FETCH_PAGE_GEOMETRY_PRIORITY,
  MAX_PAGES_CACHE_WINDOW,
  CACHE_WINDOW_FOR_ZOOMLEVEL_1
} from "constants/constants";
import * as markers from "constants/perf-markers";
import { getPageZoomLevel, getPageViewModifier } from "selectors/zoom";
import { getShowSearchInputUI, getSearchManager } from "selectors/searchSelectors";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isFirstDCAPIRenditionLoaded, getDCAPIRenditionSize, getDCAPIRenditionFormat } from "selectors/dcapiSelectors";
import { getPagesCacheManager, getThumbnailsCacheManager } from "cache/cacheProvider";
import { docAPIResponseHandler } from "utils/docAPIResponseHandler";
import { trackFirstPageLoaded, trackYouTubeLinkCount } from "analytics/events";
import { processAccessibilityNodes, processParaAccessibilityNodes } from "./accessibility";
import { isTelemetryEnabled } from "utils/Telemetry";
import {
  getThumbnailLoadStatus,
  getThumbnailZoomLevelForAJS,
  getThumbnailZoomLevel
} from "selectors/thumbnailSelectors";
import {
  getPageLoadStatus,
  isPageBlank,
  getPageHyperlinkLoadStatus,
  getPageHyperlinkLoadPriority,
  getPageHyperlinkPromise,
  getPageHyperlinkRects,
  getPageTextLoadStatus,
  getPageTextLoadPriority,
  getPageRotation,
  getPage
} from "selectors/pageSelectors";
import { AJSMetadataStatus, SecondaryMetadataStatus, ScrollDirection } from "constants/enums";
import { recordFirstTileLoadedForPage, recordPagePartiallyLoaded } from "utils/performance";
import { ajsMakeCancellablePromise } from "dc-preview-utils/src/Utils/AJSCancellablePromise";
import {
  getAnnotationFlagsUtil,
  transformRectPDFToView,
  getTileRectAccordingToMediaBox,
  getMaxZoomLevelForPageArea,
  getDynamicCanvasContainerId,
  checkIfProgressiveRenderingPossible
} from "utils/PageUtils";
import { printHandler } from "print/printHandler";
import { getScrollBarSize } from "utils/DomUtils";
import { isFirstExternalAPIRenditionLoaded } from "selectors/externalSelectors";
import { triggerPageMouseLeaveEvent } from "publicApis/Events/events";
import { getCurrentPageIndex, getPageIndexForSlideMode } from "selectors/pageNumber";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  getShouldRenderAcroForm,
  getTotalPages,
  getFileSize,
  isFullScreenOrFitOnePageMode,
  isFullScreenMode,
  getFirstRasterSize,
  getNumLoadedCachedPages,
  getCacheBurstInfoForLogging
} from "selectors/documentSelectors";
import { populatePagesGeometryData } from "utils/PageUtils";
import { pagesInView, thumbnailsInView } from "selectors/pagedViewSelectors";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { getPageOverlayApis } from "selectors/overlayDropinSelectors";
import { getHCColorPallete } from "selectors/highContrastSelectors";
import { isReadAloudEnabled } from "selectors/readAloudSelectors";
import { cancellablePromise } from "utils/CancellablePromise";
import { isFirefox } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { shouldEnableFitOneFullPageMode } from "selectors/pageByPageSelectors";
import { envUtil } from "dc-core";
import { getFeatureFlagApis } from "../publicApis/featureFlag";
import { logCacheBurstInfo } from "logging/index";
import { getShouldEnableVoiceAnnotations } from "../selectors/overlayDropinSelectors";
import { overlayEvents } from "overlays/OverlayEvents";

class DocumentStore {
  // keeps track if some failure occured during loading document
  @observable documentLoadFailed;
  // if document load fails this holds the reason for failure
  @observable documentLoadFailReason;
  @observable viewMode;
  @observable pages;
  @observable title;
  @observable pdfTitleAJS;
  @observable fileName;
  @observable hasBookmarks;
  @observable hasAttachments;
  @observable hasDigSig;
  @observable fileExtension;
  @observable shouldCreatePrintIframe;
  @observable pdfLanguage;
  @observable shouldRecomputePageSizes;
  @observable useReactVirtualized;
  @observable shouldRenderAcroForm;
  @observable assetURI;
  @observable alternateAssetUri;
  @observable isAlternateAssetURIAvailable;
  @observable fileMetaData;
  @observable dialogOpened;
  @observable isTilingEnabled;
  @observable haveMadeFirstLoadPageAJSCall;
  @observable displayDocTitle;
  @observable isXFA;
  @observable hasAcroFormFields;
  @observable displayToast;
  @observable shouldAutoHideToast;
  @observable isMouseOverToast;
  @observable ajsMetadataStatus;
  @observable secondaryMetaDataStatus;
  // Setting currPageIndex = 0 so that first page opens while slide mode is enabled. Putting it here because for goToLocation and other page jump APIs, we need to validate pageIndex and those isValidPageIndex utils are here.
  @observable currPageIndexForSlideMode;
  @observable currPageIndexForFitOnePageMode;
  @observable docViewBgColorBeforeFileLoad;
  @observable docViewBgColorAfterFileLoad;
  @observable docPermissions;
  @observable docViewRects;
  @observable isPageByPageViewMode;
  @observable shouldUnblockUIFeatures;
  @observable showQuickTools;
  @observable showRhp;
  @observable secondaryComponent;
  @observable secondaryFlyoutComponent;
  @observable isCoverPageMode;
  @observable isViewWithScrolling;
  @observable rotateState;
  @observable isTwoColumnViewMode;
  @observable shouldShowPageLoader;
  @observable forceShowCommentingTools;
  @observable isCropMode;
  @observable pagesInDOM;
  @observable lastOpenedPage;
  @observable docOpenActionsCompleted;
  @observable showStickyTools;
  @observable bottomPanelHeight;
  @observable stickyToolsLoaded;
  @observable progressivelyRenderedPages;
  @observable detectedPDFLanguage;
  @observable displayPermissionToast;
  @observable viewDocumentProperties;
  @observable documentBottomSpace;
  cacheBurstInfo;
  pagesCacheWindow;

  fileSize;
  firstRasterSize;
  docInfo;
  namedIcons;
  destinationNameToAction;
  needAppearances;
  fieldCalculationOrder;
  pdfVersion;

  docOpenActions;
  docDidSaveActions;
  docWillSaveActions;
  docDidPrintActions;
  docWillPrintActions;
  docWillCloseActions;
  permittedOperations;
  shouldTriggerCalculate;
  baseURL;
  useProgressiveRendering;
  progressiveRenderInterval;
  progressiveRenderMaxInterval;
  renderIntervalMultiplier;
  isFTPDF;
  isTaggedPDF;
  fileStorageType;
  fileShareType;
  perfMetadata;
  numberOfProgressivelyRenderedPages;
  getPageRenditionInfo;
  logGetPageRenditionInfo;
  logEarlyCacheBurstInfo;
  cacheHitCount;
  cacheMissCount;
  initialPageLoadTime;
  scrollDirection;
  lastScrollTop;
  isFilteredDocOpenActionsPresent;
  hasFASFields;
  assetPageCount;
  isImageCurrentSelection;
  maxTotalPagesIndex;

  defaultPageActionsValue = {
    jsAction: {},
    nonJsActions: {}
  };

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.documentLoadFailed = false;
    this.documentLoadFailReason = "";
    this.viewMode = docViewModes.CLASSIC_VIEW;
    this.pages = [];
    this.title = "";
    this.pdfTitleAJS = "";
    this.fileName = "";
    this.hasBookmarks = false;
    this.hasAttachments = false;
    this.hasDigSig = false;
    this.fileExtension = "";
    this.shouldCreatePrintIframe = printHandler.shouldUseBrowserPrint();
    this.pdfLanguage = "";
    this.shouldRecomputePageSizes = false;
    this.useReactVirtualized = true;
    this.shouldRenderAcroForm = true;
    this.assetURI = "";
    this.alternateAssetUri = "";
    this.isAlternateAssetURIAvailable = false;
    this.fileMetaData = "";
    this.dialogOpened = false;
    this.isTilingEnabled = false;
    this.haveMadeFirstLoadPageAJSCall = false;
    this.fileSize = undefined;
    this.displayDocTitle = false;
    this.isXFA = false;
    this.hasAcroFormFields = false;
    this.displayUnsupportedFeatureAlert = true;
    this.displayOpenInDesktopOnUnsupportedNotification = false;
    this.displayPermissionToast = false;
    this.viewDocumentProperties = true;
    this.displayToast = false;
    this.shouldAutoHideToast = false;
    this.isMouseOverToast = false;
    this.ajsMetadataStatus = AJSMetadataStatus.UNKNOWN;
    this.secondaryMetaDataStatus = SecondaryMetadataStatus.UNKNOWN;
    this.currPageIndexForSlideMode = 0;
    this.currPageIndexForFitOnePageMode = 0;
    this.needAppearances = false;
    this.docViewBgColorBeforeFileLoad = "";
    this.docViewBgColorAfterFileLoad = "";
    this.docPermissions = 0;
    this.docViewRects = {};
    this.docInfo = {};
    this.permittedOperations = {};
    this.baseURL = "";
    this.useProgressiveRendering = false;
    this.fieldCalculationOrder = [];

    // Initialising with null to check if data is present or not in stores
    this.namedIcons = null;
    this.destinationNameToAction = null;

    this.shouldUnblockUIFeatures = false;
    // TODO: Durable Link check to be removed once we start receiving it from View SDK Config
    this.showQuickTools = false;
    this.showStickyTools = false;
    this.showRhp = true;
    this.secondaryComponent = null;
    this.secondaryFlyoutComponent = null;
    this.isCoverPageMode = false;
    this.isViewWithScrolling = true;
    this.rotateState = 0;
    this.shouldShowPageLoader = true;
    this.showUnsupportedFieldAlert = true;
    this.forceShowCommentingTools = false;
    this.isCropMode = false;
    this.pagesInDOM = {};
    this.lastOpenedPage = null;
    this.detectedPDFLanguage = undefined;

    // non-observables
    this.triggerDocumentViewUpdateEvent = true;
    this.selectivelyRenderAnnotsFlag = undefined;
    this.hiddenOCGs = [];
    this.textSelectionPageRange = { startPage: -1, endpage: -1 };
    this.enableFreetext = false;
    this.isPasswordProtected = false;
    this.isDocumentEncrypted = false;
    this.documentHasSigPerms = false;
    this.oldPagesInView = {};
    this.isContinuousViewMode = false;
    this.isPageByPageViewMode = false;
    this.isEditingAllowed = false;
    this.isTwoColumnViewMode = false;
    this.latestHitTestResponse = {};
    this.isPortfolio = false;
    this.isLinearizedWorkflow = false;
    this.jsActionsAnalyticsData = {};
    this.isFTPDF = false;
    this.isTaggedPDF = false;
    this.fileStorageType = undefined;
    this.fileShareType = undefined;
    this.perfMetadata = {};
    this.pdfVersion = undefined;
    this.numberOfProgressivelyRenderedPages = 0;
    this.triggerUnsupportedFieldEvent = true;

    // non observables for CNPDF
    this.updateObjectCount = 0;

    // actions
    this.docOpenActions = [];
    this.docDidSaveActions = [];
    this.docWillSaveActions = [];
    this.docDidPrintActions = [];
    this.docWillPrintActions = [];
    this.docWillCloseActions = [];
    this.shouldTriggerCalculate = true;
    this.pageLevelActions = this.defaultPageActions;
    this.docOpenActionsCompleted = false;
    this.bottomPanelHeight = 0;
    this.stickyToolsLoaded = false;
    this.progressivelyRenderedPages = {};
    // wait time (in ms) between consecutive progressive renders
    this.progressiveRenderInterval = isFirefox()
      ? PROGRESSIVE_RENDERING_INTERVAL_FIREFOX
      : PROGRESSIVE_RENDERING_INTERVAL;
    // max wait time (in ms) between consecutive progressive renders
    this.progressiveRenderMaxInterval = PROGRESSIVE_RENDERING_INTERVAL_LIMIT;
    // increments progressive rendering interval as many times
    this.renderIntervalMultiplier = PROGRESSIVE_RENDERING_MULTIPLIER;
    this.getPageRenditionInfo = {
      request: {
        normalRendering: 0,
        tiling: 0,
        progressiveRendering: 0
      },
      response: {
        normalRendering: 0,
        tiling: 0,
        progressiveRendering: 0
      },
      numCacheReq: 0,
      numPagesInViewReq: 0,
      pageNumUpdateCount: 0,
      numZoomOperations: 0
    };
    this.logGetPageRenditionInfo = true;
    this.logEarlyCacheBurstInfo = true;
    this.cacheBurstInfo = {
      cacheBurstCount: 0,
      diffBwConsecutiveCacheBurst: 0,
      timeTakenInCacheFix: 0,
      numPagesCachedAtCacheBurst: 0
    };
    this.cacheHitCount = 0;
    this.cacheMissCount = 0;
    this.initialPageLoadTime = undefined;
    this.pagesCacheWindow = MAX_PAGES_CACHE_WINDOW;
    this.scrollDirection = ScrollDirection.FORWARD;
    this.lastScrollTop = 0;
    this.isFilteredDocOpenActionsPresent = false;
    this.hasFASFields = false;
    this.assetPageCount = new Promise(res => {
      this.resolveAssetPageCount = res;
    });
    this.documentBottomSpace = 0;
    this.isImageCurrentSelection = false;
    this.maxTotalPagesIndex = 0;
  }

  @action.bound setDocumentBottomSpace(value) {
    this.documentBottomSpace = value;
  }

  @action
  setStickyToolsLoaded(value) {
    this.stickyToolsLoaded = value;
  }

  @action
  setBottomPanelHeight(value) {
    this.bottomPanelHeight = value;
  }

  @action.bound
  setShouldUnblockUIFeatures(value) {
    this.shouldUnblockUIFeatures = value;
  }

  @action.bound
  customizeDocComponent(componentName, config) {
    if (componentName === appIdentifiers.FILE_NAME_TOAST) {
      this.stores.documentStore.setDisplayToast(config.visible);
    } else if (componentName === appIdentifiers.UNSUPPORTED_FEATURE_ALERT) {
      this.setDisplayUnsupportedFeatureAlert(config.visible);
    } else if (componentName === appIdentifiers.FORCE_SHOW_COMMENTING_TOOLS) {
      this.setForceShowCommentingTools(config.visible);
    } else if (componentName === appIdentifiers.OPEN_IN_DESKTOP_FOR_UNSUPPORTED_FEATURE) {
      this.setDisplayOpenInDesktopOnUnsupportedNotification(config.visible);
    } else if (componentName === appIdentifiers.DOCUMENT_PERMISSIONS_TOAST) {
      this.setDisplayPermissionToast(config.visible);
    } else if (componentName === appIdentifiers.VIEW_DOCUMENT_PROPERTIES) {
      this.setViewDocumentProperties(config.visible);
    }
  }

  @action
  setCurrPageIndexForSlideMode(pageIndex) {
    this.currPageIndexForSlideMode = pageIndex;
  }

  @action
  setCurrPageIndexForFitOnePageMode(pageIndex) {
    this.currPageIndexForFitOnePageMode = pageIndex;
  }

  @action.bound
  setDocViewRects(rects) {
    this.docViewRects = rects;
  }

  @action.bound
  setDocViewBgColorBeforeFileLoad(color) {
    this.docViewBgColorBeforeFileLoad = color;
  }

  @action.bound
  setDocViewBgColorAfterFileLoad(color) {
    this.docViewBgColorAfterFileLoad = color;
  }

  @action
  setShouldShowPageLoader(value = false) {
    this.shouldShowPageLoader = value;
  }

  setJSActionsAnalyticsData(data) {
    this.jsActionsAnalyticsData = data || this.jsActionsAnalyticsData;
  }

  triggerMouseLeaveEvent(index) {
    /*
    we will trigger leave for all those pages whose enter has been triggered but leave has not been triggered.
    Case 1: if we scroll from 1 to 10th page very fast, then 1 will be removed from Grid, so if we find only mounted pages as of now and try to trigger leave events for them, 1 page will not be included in that list and we will not get its leave event. If we add leave to unmount hook, then we may get leave events unnecessary when a page is mounted/unmounted by Grid update. Hence, we are doing it this way. We are finding those pages in entire PDF whose enter has been triggered but leave is pending.
    */
    const pages = this.pages.filter(page => {
      if (page && page.index !== index) return page.shouldTriggerEnterEvent === false;
      return false;
    });
    pages.forEach(page => {
      triggerPageMouseLeaveEvent(this.stores, page.index);
      page.setShouldTriggerEnterEvent(true);
    });
  }

  setPagesInView(pagesInView) {
    this.oldPagesInView = pagesInView;
  }

  setNamedIcons(namedIcons) {
    this.namedIcons = namedIcons;
  }

  addNamedIcon(icon) {
    if (this.namedIcons === null) {
      this.namedIcons = [];
    }
    this.namedIcons.push(icon);
  }

  setNamedDestinationToNameMap(namedDestinations) {
    if (this.destinationNameToAction === null) {
      this.destinationNameToAction = new Map();
    }
    for (let i = 0; i < namedDestinations.length; i++) {
      this.destinationNameToAction.set(namedDestinations[i].name, namedDestinations[i].actions);
    }
  }

  setDocInfoProp(property, value) {
    this.docInfo[property] = value;
  }

  setDocInfo(docInfo) {
    this.docInfo = docInfo || this.docInfo;
  }

  setPDFVersion(pdfVersion) {
    this.pdfVersion = pdfVersion;
  }

  @action
  setAcroFormVisibility(visible) {
    this.shouldRenderAcroForm = visible;
  }

  isValidPageIndex(index) {
    return index >= 0 && index < this.pages.length;
  }

  isValidPageRange(pageStart, pageEnd) {
    return pageStart <= pageEnd && this.isValidPageIndex(pageStart) && this.isValidPageIndex(pageEnd);
  }

  isValidTileIndexForPage(pageIndex, tileIndex) {
    return this.isValidPageIndex(pageIndex) && this.pages[pageIndex].isValidTileIndex(tileIndex);
  }

  @action
  setDocOpenActionsCompleted(value) {
    this.docOpenActionsCompleted = value;
  }

  _doZoomLevelMatchtoTheRequiredOne(index, zoomlevel, extraParams) {
    let value1 = getPageZoomLevel(this.stores, index);
    const fitPageZoomLevel = this.stores.zoom.getFitPageZoomLevel(index);
    if (isFullScreenMode(this.stores) && (extraParams.type === "cache" || value1 < fitPageZoomLevel)) {
      // In full screen mode
      // 1. cache calls will always be of fitPage zoomlevel.
      // 2. If user zoomed out the pdf in full screen mode, then still we are loading page bitmap in fitPage zoomlevel
      value1 = this.stores.zoom.getFitPageZoomLevel(index);
    }
    const value2 = zoomlevel;
    const delta = 0.0000001;
    return Math.abs(value1 - value2) <= delta;
  }

  async _handleProgressiveResponse(data, bitmapRenderer) {
    const responses = data.partialResponses;
    for await (const data of responses) {
      bitmapRenderer(data);
    }
  }

  _ajsLoadItem(
    itemType,
    index,
    itemIsLoaded,
    data,
    zoomLevel,
    pagesInView,
    itemLoadStatus,
    itemIsLoading,
    priority,
    extraParams,
    tileIndex
  ) {
    const shouldEnableProgressiveRendering =
      getQueryParameterFromUrl(window.location.href, "enableProgressiveRendering") === "true";
    if (shouldEnableProgressiveRendering && data.isPartial) {
      let shouldLoadBitmap = true;
      recordProgressiveRenderingFirstResponseTimeForPage(index);
      this._handleProgressiveResponse(data, partialData => {
        if (partialData.params.hasMissingText) {
          shouldLoadBitmap = false;
        } else if (!partialData.params.isPartial) {
          shouldLoadBitmap = true;
        }
        if (
          shouldLoadBitmap &&
          (this.pages[index].pageBitmapSize < partialData.params.pageImgBuffer.byteLength ||
            !partialData.params.isPartial)
        ) {
          this.pages[index].pageBitmapSize = partialData.params.pageImgBuffer.byteLength;
          this._ajsLoadBitmap(
            index,
            partialData,
            extraParams,
            itemType,
            itemIsLoaded,
            pagesInView,
            priority,
            zoomLevel,
            tileIndex
          );
        }
      });
    } else {
      this._ajsLoadBitmap(
        index,
        data,
        extraParams,
        itemType,
        itemIsLoaded,
        pagesInView,
        priority,
        zoomLevel,
        tileIndex
      );
    }
  }

  _ajsLoadBitmap(index, data, extraParams, itemType, itemIsLoaded, pagesInView, priority, zoomLevel, tileIndex) {
    if (isTelemetryEnabled()) {
      console.log("pageload reply", arguments);
      console.log(`page render time for page ${index}: ${data.timeTaken}`);
    }
    if (!extraParams) {
      extraParams = {};
      extraParams.renderTime = 0;
      extraParams.queueWaitTime = 0;
      extraParams.ajsTotalTime = 0;
      extraParams.mainToWorkerTime = 0;
      extraParams.workerToMainTime = 0;
    } else {
      if (!("renderTime" in extraParams)) {
        extraParams.renderTime = 0;
      }
      if (!("queueWaitTime" in extraParams)) {
        extraParams.queueWaitTime = 0;
      }
      if (!("ajsTotalTime" in extraParams)) {
        extraParams.ajsTotalTime = 0;
      }
      if (!("mainToWorkerTime" in extraParams)) {
        extraParams.mainToWorkerTime = 0;
      }
      if (!("workerToMainTime" in extraParams)) {
        extraParams.workerToMainTime = 0;
      }
    }
    extraParams.renderTime += data.timeTaken;
    extraParams.queueWaitTime += data.queueWaitTime;
    extraParams.ajsTotalTime += data.totalTime;
    extraParams.mainToWorkerTime += data.mainToWorkerTime;
    extraParams.workerToMainTime += data.workerToMainTime;
    const resp = data.data;

    if (
      resp &&
      (itemType === "thumbnail" || this._doZoomLevelMatchtoTheRequiredOne(index, resp.zoomLevel, extraParams))
    ) {
      if (isTelemetryEnabled()) {
        console.log("Useful");
      }
      if (itemType === "page" && resp.isPageProgressivelyRendered) {
        const { t5Time } = data;
        const blob = resp.pageBlob;
        const bitmapUrl = window.URL.createObjectURL(blob);
        const bitmapSize = blob.size;

        const { firstBitRenderTime, lastBitRenderTime, blobConversionTime } = resp;
        extraParams.blobConversionTime = Math.round(blobConversionTime) || 0;
        extraParams.firstAndLastBitTimeDelta = lastBitRenderTime - firstBitRenderTime;
        extraParams.isPageProgressivelyRendered = true;
        extraParams.t5Time = t5Time;
        extraParams.index = index;
        this.trackProgressiveRenderingPerf(resp, extraParams);
        itemIsLoaded({
          pageIndex: index,
          bitmapUrl,
          bitmapSize,
          pagesInView,
          pageLoader: "ajs",
          extraParams,
          isPartial: resp.isPartial,
          priority,
          zoomLevel
        });
        this.getPageRenditionInfo.response.progressiveRendering++;
      } else {
        const bitmapUrl = createURLFromBuffer(resp.buffer, resp.mimeType);
        const height = resp.height ? resp.height / getDevicePixelRatio() : 0;
        const width = resp.width ? resp.width / getDevicePixelRatio() : 0;
        itemIsLoaded({
          pageIndex: index,
          bitmapUrl,
          height,
          width,
          bitmapSize: resp.buffer.byteLength,
          pagesInView,
          pageLoader: "ajs",
          extraParams,
          isPartial: data.data.isPartial,
          priority,
          zoomLevel,
          tileIndex
        });
        if (itemType === "page") {
          if (!isUndefined(tileIndex) && tileIndex >= 0) {
            this.getPageRenditionInfo.response.tiling++;
          } else {
            this.getPageRenditionInfo.response.normalRendering++;
          }
        }
      }
    } else if (isTelemetryEnabled()) {
      console.trace("Wasted");
    }
  }

  _removeDynamicCanvasOnUnload(index) {
    const canvasContainer = document.getElementById(getDynamicCanvasContainerId(index));
    if (canvasContainer) {
      canvasContainer.remove();
    }
  }

  @action
  setUseTilingForPage(pageIndex) {
    if (this.isValidPageIndex(pageIndex)) {
      this.pages[pageIndex].useTiles = true;
    }
  }

  @action
  setAJSMetaDataStatus(status) {
    this.ajsMetadataStatus = status;
  }

  @action
  setSecondaryMetaDataStatus(status) {
    this.secondaryMetaDataStatus = status;
  }

  _logAJSPageTiming(index, extraParams, pageLoadStatus) {
    recordPageLoadEndTime(index);
    if (extraParams && extraParams.renderTime && !extraParams.isPageProgressivelyRendered) {
      recordPageRenderTime(index, extraParams);
    }
    if (!isInitialPageLoaded(this.stores) && pageLoadStatus === loadStatus.LOADED) {
      const { firstAndLastBitTimeDelta, blobConversionTime = 0 } = extraParams;
      if (extraParams.isPageProgressivelyRendered) {
        recordFirstPageProgressiveAdditionalTimes({ firstAndLastBitTimeDelta, blobConversionTime });
      }
      recordFirstPageLoaded(blobConversionTime || 0);
      let firstRasterSize;
      if (!isFirstDCAPIRenditionLoaded(this.stores) && !isFirstExternalAPIRenditionLoaded(this.stores)) {
        logFirstRenditionTypeVisible(getFirstPageLoadTime(), "AJS");
      } else {
        firstRasterSize = getFirstRasterSize(this.stores);
      }

      allSettled([
        delayedRejectablePromise(getFeatureFlagApis().getFeatureFlag("dc-perf"), 2000),
        delayedRejectablePromise(this.initialPageRenderedPromise(), 5000)
      ])
        .then(([perfMetadataResult, initialPageRenderedResult]) => {
          if (perfMetadataResult.status === "fulfilled" && perfMetadataResult.value) {
            const perfMetadata = perfMetadataResult.value;

            this.stores.documentStore.setFileShareType(perfMetadata.shareType);
            this.stores.documentStore.setFileStorageType(perfMetadata.storageType);
            this.stores.documentStore.setPerfMetadata(perfMetadata);
          } else if (perfMetadataResult.status === "rejected") {
            if (perfMetadataResult.reason === "timeout") {
              if (!this.fileShareType) {
                this.stores.documentStore.setFileShareType("timeout");
              }
              if (!this.fileStorageType) {
                this.stores.documentStore.setFileStorageType("timeout");
              }
            } else {
              if (!this.fileShareType) {
                this.stores.documentStore.setFileShareType(null);
              }
              if (!this.fileStorageType) {
                this.stores.documentStore.setFileStorageType(null);
              }
            }
          }
        })
        .finally(() => {
          trackFirstPageLoaded();

          const recordedTimings = getTimingsToLogWithFirstPageLoad(index);
          let logData = {
            ...recordedTimings,
            totalPages: this.pages.length,
            initialPageIndex: index,
            fileSize: this.fileSize
          };
          if (firstRasterSize) {
            logData.firstRasterSize = firstRasterSize;
          }
          logFirstPageLoaded(logData, this.stores);
        });

      if (
        envUtil.getEnvVar("app_identifier") === "dc-web-app" ||
        envUtil.getEnvVar("app_identifier") === "dc-web-pwa"
      ) {
        logResourceMarkers(this.stores);
      }
    }
    if (extraParams && extraParams.firstPageReloaded) {
      recordFirstPageReloadTime();
    }
    this._initiatePerfLogging(index);
  }

  _initiatePerfLogging(index) {
    when(
      () => this.shouldUnblockUIFeatures,
      () => initiatePerfLogging(this.stores, this.docApi, index, this.pages.length)
    );
  }

  _dcapiLoadItem(itemType, index, itemIsLoaded, data, zoomLevel, pagesInView, itemIsLoading) {
    if (!this.docApi.getAJSMetaDataSucceeded()) {
      const resp = data.params;
      if (resp) {
        const zoomLevel = itemType === "thumbnail" ? getThumbnailZoomLevel(this.stores, index) : 1;
        const imgUrl = createURLFromBuffer(resp.pageImgBuffer, resp.mimeType);

        ajsMakeCancellablePromise(getImageDimensionsFromBlobUrl(imgUrl))
          .then(imgDimensions => {
            const height = imgDimensions.height ? imgDimensions.height * zoomLevel : DEFAULT_PAGE_HEIGHT;
            const width = imgDimensions.width ? imgDimensions.width * zoomLevel : DEFAULT_PAGE_WIDTH;
            itemIsLoaded({
              pageIndex: index,
              bitmapUrl: imgUrl,
              height,
              width,
              bitmapSize: resp.pageImgBuffer.byteLength,
              pagesInView,
              pageLoader: "dcApi"
            });
          })
          .catch(noop);
      } else if (
        data.code &&
        data.code >= httpErrorCodes.BAD_REQUEST &&
        this.docApi.getFiletype() !== "application/pdf"
      ) {
        this.stores.documentViewStore.setNoPreviewErrorCode(data.code);
        this.setViewMode(docViewModes.NO_PREVIEW);
      }
      this._logDCAPIPageTiming(index);
    }
  }

  _logDCAPIPageTiming(index) {
    recordExternalPageLoadEndTime(index);
    if (getViewMode(this.stores) === docViewModes.NON_PDF) {
      this._initiatePerfLogging(index);
    }
  }

  _setAccData(data, pageIndex) {
    let accData = data;
    const beforeA11yTreeRenderCallback = this.stores.accessibilityStore.beforeA11yTreeRenderCallback;
    if (beforeA11yTreeRenderCallback instanceof Function) {
      accData = beforeA11yTreeRenderCallback(data, pageIndex);
    }
    this.setPageAccText(accData, pageIndex);
  }

  @action
  setShouldCreatePrintIframe(shouldCreate) {
    this.shouldCreatePrintIframe = shouldCreate;
  }

  @action
  setLoadFailed(failureReason) {
    this.documentLoadFailed = true;
    this.documentLoadFailReason = failureReason;
  }

  @action
  setdialogOpened(value) {
    this.dialogOpened = value;
  }

  @action
  unsetLoadFailure() {
    this.documentLoadFailed = false;
    this.documentLoadFailReason = "";
  }

  @action
  setPageTextLoadStatus(pageIndex, status) {
    this.pages[pageIndex].pageTextLoadStatus = status;
  }

  @action.bound
  loadPageAccText(pageIndex, priority = ACCESSIBILITY_CACHE_PRIORITY, options = {}) {
    const getWordRects = isReadAloudEnabled(this.stores) || options.wordRects;
    const getFormFieldData = !getShouldRenderAcroForm(this.stores);

    const page = this.pages[pageIndex];
    const pageTextLoadStatus = getPageTextLoadStatus(this.stores, pageIndex);
    const pageTextLoadPriority = getPageTextLoadPriority(this.stores, pageIndex);
    if (
      pageTextLoadStatus === loadStatus.NOT_LOADED ||
      (pageTextLoadStatus === loadStatus.BEING_LOADED && priority < pageTextLoadPriority)
    ) {
      page.pageTextLoadStatus = loadStatus.BEING_LOADED;
      const accPromise = initialPageLoadedPromise(this.stores)
        .then(() => this.docApi.extractPageText(pageIndex, getWordRects, getFormFieldData, priority))
        .then(data => {
          this._setAccData(data, pageIndex);
        })
        .catch(err => {
          this.setPageTextLoadStatus(pageIndex, loadStatus.FAILED);
          return Promise.reject(err);
        });
      /* Whenever a higher priority call comes, the lower priority call is simply removed from linked
       * list in acrobatJS. We do not receive any Resolve / Reject on its promise.
       * So, if we await on a such a call, the promise will go into a permanent pending state.
       * As, a temporary fix, we have kept converted all promises to Cancellable promises and stored them in page.pageAccPromise .
       * On, completion of higher priority call, we would either resolve all lower priority calls, or reject
       * them based on the result of higher priority call. This resolve/ reject is done through cancellablePromise function.
       */
      page.pageAccPromise = cancellablePromise(accPromise, page.pageAccPromise);
      page.pageTextLoadPriority = priority;
    }
    return page.pageAccPromise ? page.pageAccPromise.promise : undefined;
  }

  @action
  unloadPageAccData(pageIndex) {
    this.pages[pageIndex].pageTextLoadStatus = loadStatus.NOT_LOADED;
  }

  @action.bound
  loadPageHyperlinks(pageIdx, priority) {
    this.pages[pageIdx].hyperlinkLoadStatus = loadStatus.BEING_LOADED;
    this.pages[pageIdx].hyperlinkLoadPriority = priority;
    const hyperlinkPromise = this.docApi.getHyperlinkAnnots(pageIdx, priority).then(data => {
      this.setHyperlinkRects(data, pageIdx);
      return getPageHyperlinkRects(this.stores, pageIdx);
    });
    this.pages[pageIdx].hyperlinkPromise = hyperlinkPromise;
  }

  _shouldLoadHyperlinks(currLoadPriority, pageIdx) {
    const linkLoadStatus = getPageHyperlinkLoadStatus(this.stores, pageIdx);
    if (
      linkLoadStatus === loadStatus.NOT_LOADED ||
      (linkLoadStatus === loadStatus.BEING_LOADED &&
        getPageHyperlinkLoadPriority(this.stores, pageIdx) > currLoadPriority)
    ) {
      return true;
    }
    return false;
  }

  loadAndGetPageHyperlinks(pageIdx, priority) {
    if (this._shouldLoadHyperlinks(priority, pageIdx)) {
      this.loadPageHyperlinks(pageIdx, priority);
    }
    return getPageHyperlinkPromise(this.stores, pageIdx);
  }

  @action
  setHyperlinkRects(data, pageIdx) {
    const linkRects = [];
    let annotList = data.data.annots;
    annotList &&
      annotList.forEach(annot => {
        linkRects.push(annot.docRect);
      });
    this.pages[pageIdx].hyperlinkLoadStatus = loadStatus.LOADED;
    this.pages[pageIdx].setHyperlinkRects(linkRects);
  }

  @action
  setPageAccText(data, pageIndex) {
    let json = data.data.text;
    if (typeof json !== "object") {
      json = JSON.parse(data.data.text);
    }
    const { aNodes, paraNodes } = json || {};
    if (aNodes && aNodes.length) {
      const returnVal = processAccessibilityNodes(aNodes[0].children);
      this.updatePageAccText(returnVal, pageIndex);
      this.stores.accessibilityStore.setTabbablePDFElements(pageIndex, returnVal);
    }
    if (paraNodes) {
      const returnVal = processParaAccessibilityNodes(paraNodes);
      this.updateParaAccText(returnVal, pageIndex);
    }
    this.updatePageRotation(getPageRotation(this.stores, pageIndex), pageIndex);
    this.pages[pageIndex].pageTextLoadStatus = loadStatus.LOADED;
  }

  @action
  updatePageAccText(data, pageIndex) {
    this.pages[pageIndex].pageAccText = data;
  }
  @action
  updatePageRotation(rotation, pageIndex) {
    this.pages[pageIndex].rotation = rotation;
  }

  @action
  updateParaAccText(data, pageIndex) {
    this.pages[pageIndex].paraAccText = data;
  }

  @action
  setDocPermissions(docPermissions) {
    this.docPermissions = docPermissions;
  }

  setIsDocumentEncrypted(isEncrypted) {
    this.isDocumentEncrypted = !!isEncrypted;
  }

  setDocumentHasSigPerms(hasSigPerms) {
    this.documentHasSigPerms = !!hasSigPerms;
  }

  setIsFilePasswordProtected(value) {
    this.isPasswordProtected = value;
  }

  setIsFTPDF(value) {
    this.isFTPDF = value;
  }

  setIsTaggedPDF(value) {
    this.isTaggedPDF = value;
  }

  @action
  setAssetURI(assetURI) {
    this.assetURI = assetURI;
  }

  @action
  setAlternateAssetURI(alternateAssetUri) {
    this.alternateAssetUri = alternateAssetUri;
  }

  @action
  setIsAlternateAssetURIAvailable(value) {
    this.isAlternateAssetURIAvailable = value;
  }

  @action
  setPagesGeometry(pagesGeometry) {
    if (pagesGeometry.length > 0) {
      let hasPagesGeometryChanged = false;
      const defaultPageBoxes = pagesGeometry[0].boxes ? pagesGeometry[0].boxes : { mediaBox: [], cropBox: [] };
      for (let i = 0; i < this.pages.length; i++) {
        const pageGeometry = pagesGeometry[i] ? pagesGeometry[i] : pagesGeometry[0];
        this.pages[i].origHeight = pageGeometry.origHeight;
        this.pages[i].origWidth = pageGeometry.origWidth;
        this.pages[i].tMatrix = pageGeometry.tMatrix;
        this.pages[i].minZoom = pageGeometry.minZoom;
        this.pages[i].maxZoom = pageGeometry.maxZoom;
        this.pages[i].boxes.mediaBox = pageGeometry.boxes ? pageGeometry.boxes.mediaBox : defaultPageBoxes.mediaBox;
        this.pages[i].boxes.cropBox = pageGeometry.boxes ? pageGeometry.boxes.cropBox : defaultPageBoxes.cropBox;

        if (
          this.pages[i].origHeight !== this.pages[0].origHeight ||
          this.pages[i].origWidth !== this.pages[0].origWidth
        ) {
          hasPagesGeometryChanged = true;
          this.unloadPage(i);
        }
      }
      this.setShouldRecomputePageSizes(hasPagesGeometryChanged);
    }
  }
  /* This function set the height, width of the pages according to cropBox/mediaBox depending on the flag */
  resetPageOrigHeightWidth(
    startIndex,
    endIndex,
    shouldUseMediaBox /* This flag is true when we want to change geometry according to mediaBox*/
  ) {
    const boxKey = shouldUseMediaBox ? "mediaBox" : "cropBox";
    for (let i = startIndex; i <= endIndex; i++) {
      const boxes = this.pages[i].boxes;
      const currentPage = this.pages[i];
      if (boxes !== undefined && boxes.cropBox !== boxes.mediaBox) {
        const [left, bottom, right, top] = boxes[boxKey];
        const rotation = getPageRotation(this.stores, i);
        const rightLeftDiff = right - left;
        const topBottomDiff = top - bottom;
        if (rotation === 90 || rotation === 270) {
          currentPage.origHeight = rightLeftDiff;
          currentPage.origWidth = topBottomDiff;
        } else {
          currentPage.origHeight = topBottomDiff;
          currentPage.origWidth = rightLeftDiff;
        }
        currentPage.maxZoom = getMaxZoomLevelForPageArea(currentPage.origHeight, currentPage.origWidth);
        this.revokePage(i);
      }
    }
  }

  @action
  setPagesGeometryViaMediaBox() {
    this.resetPageOrigHeightWidth(0, this.pages.length - 1, true);
  }

  @action
  setPagesGeometryViaCropBox() {
    this.resetPageOrigHeightWidth(0, this.pages.length - 1, false);
  }

  @action
  resetPagesGeometry(startIndex = 0, endIndex = -1, pagesGeometry = []) {
    this.resetPageOrigHeightWidth(0, startIndex - 1, false);
    if (pagesGeometry.length > 0) {
      for (let i = startIndex; i <= endIndex; i++) {
        let currentPage = this.pages[i];
        const pageGeometry = pagesGeometry[i] ? pagesGeometry[i] : pagesGeometry[0];
        currentPage.origHeight = pageGeometry.origHeight;
        currentPage.origWidth = pageGeometry.origWidth;
        currentPage.tMatrix = pageGeometry.tMatrix;
        currentPage.minZoom = pageGeometry.minZoom;
        currentPage.maxZoom = pageGeometry.maxZoom;
        currentPage.boxes.mediaBox = pageGeometry.boxes ? pageGeometry.boxes.mediaBox : pagesGeometry[0].boxes.mediaBox;
        currentPage.boxes.cropBox = pageGeometry.boxes ? pageGeometry.boxes.cropBox : pagesGeometry[0].boxes.cropBox;
        this.revokePage(i);
      }
    }
    this.resetPageOrigHeightWidth(endIndex + 1, this.pages.length - 1, false);
  }

  @action
  setViewRotation(rotation) {
    const currentPageIndex = this.getCurrentPageIndex();
    this.rotateState = (this.rotateState + rotation) % 360;
    this.stores.pageHighlightsStore.rotateHighlights(rotation);
    const searchManager = getSearchManager(this.stores);
    if (searchManager) {
      searchManager.rotateSearchResults(rotation);
    }
    const ViewModifiers = this.stores.zoom.pageViewModifier;
    this.stores.zoom.setPageViewModifier({ newPageViewModifier: ViewModifiers });
    this.setShouldRecomputePageSizes(true);
    if (currentPageIndex >= 0) {
      this.stores.pagedViewState.goToPage(currentPageIndex + 1);
    }
  }

  @action
  resetViewRotation() {
    this.stores.pageHighlightsStore.rotateHighlights((360 - this.rotateState) % 360);
    const searchManager = getSearchManager(this.stores);
    if (searchManager) {
      searchManager.rotateSearchResults((360 - this.rotateState) % 360);
    }
    this.rotateState = 0;
  }

  @action
  addPages(pages) {
    pages.forEach(page => {
      this.pages.push(new Page(page));
    });
    this.maxTotalPagesIndex = Math.max(this.pages.length - 1, this.maxTotalPagesIndex);
  }

  @action
  deletePages(pageIndices) {
    pageIndices = pageIndices.reverse();
    pageIndices.forEach(pageIndex => {
      runInAction(() => {
        this.pages.splice(pageIndex, 1);
      });
    });
  }

  @action
  setMaxTotalPagesIndex(value) {
    this.maxTotalPagesIndex = Math.max(value, this.maxTotalPagesIndex);
  }

  @action
  insertPages(insertAt, pages) {
    const pagesToInsert = pages.map(page => new Page(page));
    this.pages.splice(insertAt, 0, ...pagesToInsert);
  }

  @action
  reorderPages(pageIndices, afterPageIndex) {
    let pageShift = 0;
    pageIndices.forEach(pageIndex => {
      if (pageIndex > afterPageIndex) {
        const page = this.pages[pageIndex];
        this.pages.splice(pageIndex, 1);
        this.pages.splice(afterPageIndex + 1, 0, page);

        afterPageIndex++;
      } else {
        const page = this.pages[pageIndex - pageShift];
        this.pages.splice(afterPageIndex + 1, 0, page);
        this.pages.splice(pageIndex - pageShift, 1);
        pageShift++;
      }
    });
  }

  setPDFActions(actions = {}) {
    this.docOpenActions = actions.Open || [];
    this.docDidSaveActions = actions.DidSave || [];
    this.docWillSaveActions = actions.WillSave || [];
    this.docDidPrintActions = actions.DidPrint || [];
    this.docWillPrintActions = actions.WillPrint || [];
    this.docWillCloseActions = actions.WillClose || [];
  }

  setShouldTriggerCalculate(shouldTrigger) {
    this.shouldTriggerCalculate = shouldTrigger;
  }

  @action
  setMetadata(metadata) {
    this.title = metadata.title || this.title;
    this.pdfTitleAJS = metadata.pdfTitle;
    this.fileExtension = metadata.fileExtension || this.fileExtension;
    this.pdfLanguage = metadata.language || "";
    this.hasBookmarks = metadata.hasBookmarks || this.hasBookmarks;
    this.hasAttachments = metadata.hasAttachments || this.hasAttachments;
    this.isEditingAllowed = metadata.isEditingAllowed || this.isEditingAllowed;
    this.isPortfolio = metadata.isPortfolio || this.isPortfolio;
    this.hasAcroFormFields = metadata.hasAcroFormFields || this.hasAcroFormFields;
    this.needAppearances = metadata.needAppearances || this.needAppearances;
    this.fieldCalculationOrder = metadata.co || this.fieldCalculationOrder;
    metadata.actions && this.setPDFActions(metadata.actions);
    this.setFileSize(metadata.fileSize);
    this.setFileName(metadata.fileName);
    this.setBaseUrl(metadata.baseURL);
    this.setHasDigSig(metadata.hasDigitalSignatures);
    this.setDocInfo(metadata.docInfo);
    this.setPDFVersion(metadata.pdfVersion);
    this.pageLevelActions = metadata.pageLevelActions || this.defaultPageActionsValue;
    if (metadata.displayDocTitle) {
      this.displayDocTitle = true;
    }
    if (metadata.isXFA) {
      this.isXFA = true;
    }
    this.resolveAssetPageCount(metadata.numPages ? metadata.numPages : null);
  }

  @action
  setHasDigSig(value) {
    this.hasDigSig = value || this.hasDigSig;
  }

  async fetchDetectedPDFLanguage() {
    const detectedLanguage = await this.docApi.detectLanguage();
    this.setDetectedPDFLanguage(detectedLanguage);
  }

  @action.bound
  setDetectedPDFLanguage(detectedLanguage) {
    this.detectedPDFLanguage = detectedLanguage;
  }

  setFileSize(fileSize) {
    this.fileSize = fileSize || this.fileSize;
  }

  setFirstRasterSize(size) {
    this.firstRasterSize = size || this.firstRasterSize;
  }

  @action
  setFileName(fileName) {
    this.fileName = fileName || this.fileName;
  }

  @action
  setFileMetaData(metadata) {
    if (metadata) {
      this.fileMetaData = metadata;
    }
  }

  @action.bound
  tileIsLoading(pageIndex, tileIndex) {
    if (this.isValidTileIndexForPage(pageIndex, tileIndex)) {
      this.pages[pageIndex].pageLoadStatus = loadStatus.BEING_LOADED;
      this.pages[pageIndex].pageTiles[tileIndex].tileLoadStatus = loadStatus.BEING_LOADED;
    }
  }

  @action.bound
  pageIsLoading(index) {
    if (this.isValidPageIndex(index)) {
      this.pages[index].pageLoadStatus = loadStatus.BEING_LOADED;
    }
  }

  @action.bound
  thumbnailIsLoading(index) {
    if (this.isValidPageIndex(index)) {
      this.pages[index].thumbnailLoadStatus = loadStatus.BEING_LOADED;
    }
  }

  @action.bound
  tileIsLoaded({
    pageIndex,
    bitmapUrl,
    height,
    width,
    bitmapSize,
    pagesInView,
    pageLoader, // "ajs" or "dcApi"
    extraParams = {},
    isPartial,
    priority,
    zoomLevel,
    tileIndex = -1
  }) {
    if (this.isValidTileIndexForPage(pageIndex, tileIndex)) {
      const page = this.pages[pageIndex];
      const oldBitmapUrl = page.pageTiles[tileIndex].tileBitmap;
      page.pageTiles[tileIndex].tileBitmap = bitmapUrl;
      page.pageTiles[tileIndex].tileLoadStatus = loadStatus.LOADED;

      if (oldBitmapUrl) {
        URL.revokeObjectURL(oldBitmapUrl);
      }

      if (tileIndex === 0) {
        recordFirstTileLoadedForPage(pageIndex);
      }

      if (page.haveAllTilesLoaded) {
        this.allPageTilesLoaded(pageIndex, pagesInView, pageLoader, extraParams);
      }
    }
  }

  performPostPageLoadOps(pageIndex, extraParams, pageLoadStatus) {
    this._logAJSPageTiming(pageIndex, extraParams, pageLoadStatus);
    if (isInitialPageLoaded(this.stores) === false) {
      this.stores.pagedViewState.setInitialPageStatus(pageIndex, true);
    }
    if (extraParams && extraParams.type === "cache" && pageLoadStatus === loadStatus.LOADED) {
      const cacheManager = getPagesCacheManager();
      if (cacheManager) cacheManager.cacheItems();
    }
  }

  trackProgressiveRenderingPerf(
    responseObj,
    { renderTime, t5Time, index, startTime, queueWaitTime, ajsTotalTime, mainToWorkerTime, workerToMainTime }
  ) {
    this.numberOfProgressivelyRenderedPages++;
    // For now we are tracking only upto 5 pages per file.
    if (this.numberOfProgressivelyRenderedPages < MAX_LOG_FOR_PER_PAGE_RENDER_TIME) {
      const {
        firstBitRenderTime,
        lastBitRenderTime,
        pageNum,
        numberOfCanvasPaints = 0,
        blobConversionTime = 0
      } = responseObj;
      const eventData = {
        FirstBitRenderTime: Math.round(firstBitRenderTime),
        LastBitRenderTime: Math.round(lastBitRenderTime),
        RequestTurnAroundTime: Math.round(renderTime),
        QueueWaitTime: Math.round(queueWaitTime),
        AjsTotalTime: Math.round(ajsTotalTime),
        MainToWorkerTime: Math.round(mainToWorkerTime),
        WorkerToMainTime: Math.round(workerToMainTime),
        PageNo: pageNum,
        NumberOfCanvasPaints: numberOfCanvasPaints,
        BlobConversionTime: Math.round(blobConversionTime),
        T5Time: Math.round(t5Time),
        isInitialPage: !isInitialPageLoaded(this.stores),
        PageRenderTime: Math.round(renderTime - blobConversionTime),
        TotalPageRenderTime: Math.round(performance.now() - startTime),
        RenderedViaTiling: false,
        PageType: "visible",
        IsPageProgressivelyRendered: true
      };
      recordProgressivePageRenderTime(index, eventData);
    }
  }

  async countLinks(
    pageStart,
    pageEnd,
    linkRegexStr = YOUTUBE_REG_PATTERN,
    priority = GET_HYPERLINK_LIST_FOR_CNT_PRIORITY
  ) {
    let promises = [];
    for (let i = pageStart; i < pageEnd; i++) {
      const promise = this.docApi.getHyperlinkAnnots(i, priority, true);
      promises.push(promise);
    }
    let validUrlCnt = 0;
    return Promise.all(promises).then(values => {
      values.forEach(value => {
        const annotList = value.data.annots;
        annotList &&
          annotList.forEach(annot => {
            if (annot.actions && annot.actions[EVENT_NAMES.MOUSE_UP]) {
              const mouseUpActions = annot.actions[EVENT_NAMES.MOUSE_UP];
              if (mouseUpActions[0] && mouseUpActions[0].actionType === "URI") {
                const linkUrl = mouseUpActions[0].actionData.URI;
                if (linkUrl && isValidUrl(linkUrl, linkRegexStr)) validUrlCnt++;
              }
            }
          });
      });
      return validUrlCnt;
    });
  }

  // TODO: below api will be removed once analytics is completed
  async countYouTubeLinks() {
    const totalPages = getTotalPages(this.stores);
    if (totalPages <= YOUTUBE_LINKS_CNT_PAGES_THRESHOLD) {
      const cnt = await this.countLinks(0, totalPages);
      if (cnt > 0) trackYouTubeLinkCount(cnt);
    }
  }

  @action.bound
  allPageTilesLoaded(pageIndex, pagesInView, pageLoader, extraParams = {}) {
    const oldPageBitmapUrl = this.pages[pageIndex].pageBitmap;
    this.pages[pageIndex].pageBitmap = "";

    if (oldPageBitmapUrl) {
      URL.revokeObjectURL(oldPageBitmapUrl);
    }

    this.pages[pageIndex].pageLoadStatus = loadStatus.LOADED;
    this.triggerClearCache(pagesInView);
    if (pageLoader === "ajs") {
      extraParams.renderedViaTiling = true;
      this.performPostPageLoadOps(pageIndex, extraParams, loadStatus.LOADED);
    }
  }

  triggerClearCache(pagesInView) {
    if (pagesInView) {
      let pagesCacheManager = getPagesCacheManager();
      if (pagesCacheManager) {
        pagesCacheManager.clearCache();
      }
    }
  }

  @action.bound
  pageIsLoaded({
    pageIndex,
    bitmapUrl,
    height,
    width,
    bitmapSize,
    pagesInView,
    pageLoader, // "ajs" or "dcApi"
    extraParams = {},
    isPartial,
    priority,
    zoomLevel
  }) {
    if (this.isValidPageIndex(pageIndex)) {
      this.pages[pageIndex].pageBitmapSize = bitmapSize;
      const oldBitmapUrl = this.pages[pageIndex].pageBitmap;
      const shouldEnableProgressiveRendering =
        getQueryParameterFromUrl(window.location.href, "enableProgressiveRendering") === "true";
      if (!shouldEnableProgressiveRendering) {
        this.pages[pageIndex].pageBitmap = (isPartial && !oldBitmapUrl) || !isPartial ? bitmapUrl : oldBitmapUrl;
      } else {
        this.pages[pageIndex].pageBitmap = bitmapUrl;
      }
      if (!isPartial) {
        this.pages[pageIndex].pageLoadStatus = loadStatus.LOADED;
      }
      if (oldBitmapUrl) {
        URL.revokeObjectURL(oldBitmapUrl);
      }

      this.triggerClearCache(pagesInView);
      if (pageLoader === "ajs" && !isPartial) {
        extraParams.renderedViaTiling = false;
        this.performPostPageLoadOps(pageIndex, extraParams, loadStatus.LOADED);
      }
      if (isPartial || extraParams.renderTime > ENABLE_TILING_DELTA) {
        this.setUseTilingForAllPages();
      }
      if (isPartial) {
        recordPagePartiallyLoaded(pageIndex);
      }
      if (pageLoader === "dcApi" && extraParams && extraParams.type === "cache") {
        const cacheManager = getPagesCacheManager();
        if (cacheManager) cacheManager.cacheItems();
      }
    }
  }

  @action.bound
  setUseTilingForAllPages() {
    // if tiling is required, currently it is enabled for all pages
    this.pages.forEach((page, index) => {
      this.setUseTilingForPage(index);
    });
  }

  @action
  loadItem(
    itemType,
    index,
    zoomLevel,
    pagesInView,
    itemLoadStatus,
    itemIsLoaded,
    itemIsLoading,
    priority = 1,
    extraParams = {},
    tileIndex = -1,
    hcColorPallete = null
  ) {
    if (this.isValidPageIndex(index) && itemLoadStatus === loadStatus.NOT_LOADED) {
      if (extraParams.type !== "cache") {
        itemIsLoading(index, tileIndex);
      }

      let renditionPromise = this.docApi.getRendition(
        itemType,
        index,
        zoomLevel,
        pagesInView.start,
        pagesInView.end,
        getDevicePixelRatio(),
        priority,
        extraParams,
        false,
        hcColorPallete
      );

      docAPIResponseHandler(
        renditionPromise,
        data => {
          this._ajsLoadItem(
            itemType,
            index,
            itemIsLoaded,
            data,
            zoomLevel,
            pagesInView,
            itemLoadStatus,
            itemIsLoading,
            priority,
            extraParams,
            tileIndex
          );
          const resp = data.data;
          const page = getPage(this.stores, index);
          page.setActionsForPage(resp && resp.actions);
        },
        () => {},
        data => {
          this._dcapiLoadItem(itemType, index, itemIsLoaded, data, zoomLevel, pagesInView, itemIsLoading);
        }
      );
    }
  }

  @action.bound
  unloadAcroformAppearance(index, shouldUnloadAppearnce) {
    this.pages[index].acroformAppearancesMap.forEach(item => {
      item.unloadAppearances(shouldUnloadAppearnce);
    });
  }

  @action.bound
  unloadPage(index) {
    if (this.isValidPageIndex(index)) {
      this.pages[index].pageLoadStatus = loadStatus.NOT_LOADED;
      this.pages[index].pageUsageStatus = pageUsageStatus.NOT_USED;
      this.unloadAcroformAppearance(index);
      if (this.pages[index].useTiles) {
        this.pages[index].pageTiles.forEach(tile => {
          tile.tileLoadStatus = loadStatus.NOT_LOADED;
        });
      }
      this._removeDynamicCanvasOnUnload(index);
    }
  }

  @action.bound
  unloadThumbnail(index) {
    if (this.isValidPageIndex(index)) {
      this.pages[index].thumbnailBitmapSize = 0;
      this.pages[index].thumbnailLoadStatus = loadStatus.NOT_LOADED;
    }
  }

  @action.bound
  revokeThumbnail(index) {
    if (this.isValidPageIndex(index)) {
      const bitmapURL = this.pages[index].thumbnailBitmap;
      this.pages[index].thumbnailBitmap = "";
      this.pages[index].thumbnailBitmapSize = 0;
      if (bitmapURL) {
        URL.revokeObjectURL(bitmapURL);
      }
    }
  }

  _recordPageLoadStartTime(index) {
    if (this.docApi.getAJSMetaDataSucceeded() === true) {
      recordPageLoadStartTime(index);
    } else {
      recordExternalPageLoadStartTime(index);
    }
  }

  _updateCacheHitAndMissCount(index, type) {
    if (type !== "cache") {
      if (getPageLoadStatus(this.stores, index) === loadStatus.LOADED) {
        this.cacheHitCount++;
        this.pages[index].pageUsageStatus = pageUsageStatus.USED;
      } else {
        this.cacheMissCount++;
      }
    }
  }

  @action.bound
  loadPage(index, zoomLevel, pagesInView, priority = PAGE_PRIORITY, extraParams = {}) {
    if (this.isValidPageIndex(index)) {
      this._updateCacheHitAndMissCount(index, extraParams.type);
      const shouldEnableProgressiveRendering =
        getQueryParameterFromUrl(window.location.href, "enableProgressiveRendering") === "true";
      // record page load
      this._recordPageLoadStartTime(index);

      extraParams.showAnnots = this.selectivelyRenderAnnotsFlag || getAnnotationFlagsUtil();
      extraParams.hiddenOCGs = [...this.hiddenOCGs];
      extraParams.tileRect = {};
      extraParams.size = getDCAPIRenditionSize(this.stores);
      extraParams.format = getDCAPIRenditionFormat(this.stores);
      const hcColorPallete = getHCColorPallete(this.stores);
      const isProgressiveRenderingPossible = checkIfProgressiveRenderingPossible(
        {
          stores: this.stores,
          index,
          priority,
          itemType: "page",
          progressiveRenderingWorkflow: PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING
        },
        true
      );
      extraParams.startTime = performance.now();

      if (this.isCropMode) {
        const { mediaBox } = this.pages[index].boxes;
        const tileRectMediaBox = getTileRectAccordingToMediaBox(mediaBox);
        extraParams.tileRect = transformRectPDFToView(
          tileRectMediaBox[0],
          tileRectMediaBox[1],
          tileRectMediaBox[2],
          tileRectMediaBox[3],
          index,
          this.stores,
          zoomLevel,
          {
            shouldUseMediaBox: false,
            viewRotation: 0
          }
        );
      }
      // Last check ensures if page is scrolled and we try to get page with Priority 10
      // and if canvas is present, we give priority to PR instead of tiling
      if (
        this.isTilingEnabled &&
        this.pages[index].useTiles &&
        !this.isCropMode &&
        (extraParams.type === "cache" || !shouldEnableProgressiveRendering || !isPageBlank(this.stores, index)) &&
        !isProgressiveRenderingPossible
      ) {
        const tiles = this.pages[index].pageTiles;
        const tileCoords = this.pages[index].tileCoords;
        // in tiling if timelimit is sent, it may return partial page
        delete extraParams.timeLimit;
        extraParams.renderTime = 0;
        extraParams.ajsTotalTime = 0;
        extraParams.queueWaitTime = 0;
        extraParams.mainToWorkerTime = 0;
        extraParams.workerToMainTime = 0;
        tiles.forEach((tile, tileIndex) => {
          extraParams.tileRect.left = tileCoords[tileIndex].startX;
          extraParams.tileRect.top = tileCoords[tileIndex].startY;
          extraParams.tileRect.right = tileCoords[tileIndex].endX;
          extraParams.tileRect.bottom = tileCoords[tileIndex].endY;

          let tileLoadStatus;
          if (this.pages[index].pageLoadStatus === loadStatus.LOADED) {
            tileLoadStatus = loadStatus.LOADED;
          } else {
            tileLoadStatus = tile.tileLoadStatus;
          }

          this.loadItem(
            "page",
            index,
            zoomLevel,
            pagesInView,
            tileLoadStatus,
            this.tileIsLoaded,
            this.tileIsLoading,
            priority,
            extraParams,
            tileIndex,
            hcColorPallete
          );
          this.getPageRenditionInfo.request.tiling++;
        });
      } else {
        if (this.isTilingEnabled && this.haveMadeFirstLoadPageAJSCall && !this.isCropMode) {
          if (!extraParams.isFirstPageReloadCall && extraParams.type === "cache") {
            extraParams.timeLimit = CACHE_PARTIAL_LOADING_TIME_DELTA;
          }
        }

        if (this.docApi.getAJSMetaDataSucceeded()) {
          this.haveMadeFirstLoadPageAJSCall = true;
          if (
            extraParams.type !== "cache" &&
            shouldEnableProgressiveRendering &&
            isPageBlank(this.stores, index) &&
            !this.isCropMode
          ) {
            extraParams.enableProgressiveRendering = true;
          }
        }

        extraParams.renderTime = 0;
        extraParams.ajsTotalTime = 0;
        extraParams.queueWaitTime = 0;
        extraParams.mainToWorkerTime = 0;
        extraParams.workerToMainTime = 0;
        this.loadItem(
          "page",
          index,
          zoomLevel,
          pagesInView,
          this.pages[index].pageLoadStatus,
          this.pageIsLoaded,
          this.pageIsLoading,
          priority,
          extraParams,
          -1,
          hcColorPallete
        );

        if (isProgressiveRenderingPossible) {
          this.getPageRenditionInfo.request.progressiveRendering++;
        } else {
          this.getPageRenditionInfo.request.normalRendering++;
        }
      }

      if (extraParams.type === "cache") {
        this.getPageRenditionInfo.numCacheReq++;
      } else {
        this.getPageRenditionInfo.numPagesInViewReq++;
      }
    }
  }

  @action.bound
  reloadPagesInView(pagesInView) {
    if (this.isValidPageRange(pagesInView.start, pagesInView.end)) {
      for (let index = pagesInView.start; index <= pagesInView.end; index++) {
        this.unloadPage(index);
      }
      this.loadPagesInView(pagesInView);
    }
  }

  @action.bound
  reloadPages(pagesInView) {
    for (let i = 0; i < this.pages.length; i++) {
      this.unloadPage(i);
    }
    this.loadPagesInView(pagesInView);
  }

  @action.bound
  unloadAllThumbnails() {
    for (let i = 0; i < this.pages.length; i++) {
      this.unloadThumbnail(i);
    }
  }

  @action.bound
  reloadThumbnails(thumbnailsInView) {
    this.unloadAllThumbnails();
    this.loadThumbnailsInView(thumbnailsInView);
  }

  @action.bound
  revokePage(index) {
    if (this.isValidPageIndex(index)) {
      const bitmapURL = this.pages[index].pageBitmap;
      this.pages[index].pageBitmap = "";
      if (bitmapURL) {
        URL.revokeObjectURL(bitmapURL);
      }

      if (this.pages[index].useTiles) {
        this.pages[index].pageTiles.forEach(tile => {
          if (tile.tileBitmap) {
            URL.revokeObjectURL(tile.tileBitmap);
          }
          tile.tileBitmap = "";
        });
      }

      this.pages[index].acroformAppearancesMap.forEach(item => item.revokeAppearances());
    }
  }

  @action.bound
  thumbnailIsLoaded({ pageIndex, bitmapUrl, height, width, bitmapSize, thumbnailsInView, pageLoader }) {
    if (this.isValidPageIndex(pageIndex)) {
      if (pageLoader === "ajs") {
        this.pages[pageIndex].thumbnailLoadedFrom = responseSource.AJS;
      } else if (pageLoader === "dcApi") {
        this.pages[pageIndex].thumbnailLoadedFrom = responseSource.DC;
      }
      this.pages[pageIndex].thumbnailBitmap = bitmapUrl;
      this.pages[pageIndex].thumbnailHeight = height;
      this.pages[pageIndex].thumbnailWidth = width;
      this.pages[pageIndex].thumbnailLoadStatus = loadStatus.LOADED;
      this.pages[pageIndex].thumbnailBitmapSize = bitmapSize;
      let thumbnailsCacheManager = getThumbnailsCacheManager();
      if (thumbnailsCacheManager) {
        thumbnailsCacheManager.clearCache();
      }
    }
  }

  @action.bound
  loadThumbnail(index, zoomLevel, thumbnailsInView, priority = THUMBNAIL_PRIORITY, extraParams = {}) {
    if (this.isValidPageIndex(index)) {
      // showing all annots for thumbnails. TODO: update thumbanils when pdf is dirty (add or edit comments, fill form)
      extraParams.showAnnots = getAnnotationFlagsUtil();

      const hcColorPallete = getHCColorPallete(this.stores);
      this.loadItem(
        "thumbnail",
        index,
        zoomLevel,
        thumbnailsInView,
        this.pages[index].thumbnailLoadStatus,
        this.thumbnailIsLoaded,
        this.thumbnailIsLoading,
        priority,
        extraParams,
        -1,
        hcColorPallete
      );
    }
  }

  @action
  customizeHudComponents(components) {
    for (const [component, visible] of Object.entries(components)) {
      this.stores.hudStore.customizeHudComponent(appIdentifiers[component], { visible });
    }
  }

  _shouldCustomiseHUDCompAndZoomFeature(prevDocViewMode) {
    return (
      prevDocViewMode !== docViewModes.CLASSIC_VIEW &&
      prevDocViewMode !== docViewModes.TWO_COLUMN &&
      prevDocViewMode !== docViewModes.FULL_SCREEN &&
      prevDocViewMode !== docViewModes.FIT_ONE_FULL_PAGE
    );
  }

  @action
  setCommonCustomUIForClassicContinuousAndTwoColumnViewMode(
    pageIndex,
    pageViewModifier = pageViewModifiers.NO_MOD,
    prevDocViewMode
  ) {
    this.viewMode = docViewModes.CLASSIC_VIEW;
    this.stores.zoom.setFixedMargins({
      left: "auto",
      right: "auto"
    });
    if (this._shouldCustomiseHUDCompAndZoomFeature(prevDocViewMode)) {
      this.customizeHudComponents({
        DOCK: true,
        FIT_PAGE: true,
        FIT_WIDTH: true
      });
      this.stores.appStore.enableZoomFeature(true);
    }

    if (!this.stores.lhpStore.getLHPExplicitlyDisabled()) {
      this.stores.lhpStore.setLHPVisibility(true);
    }
    this.stores.appUiState.setScrollbarSize(getScrollBarSize());
    // By default show scrubber in mobile devices only
    if (isCurrentDeviceMobile()) {
      this.stores.pagedViewState.setShowScrubber(true);
    } else {
      this.stores.pagedViewState.setShowScrubber(false);
    }
    this.stores.hudStore.setIsDocked(true);

    if (!this.stores.hudStore.getHudExplicitlyDisabled()) {
      this.stores.hudStore.setHudDisable(false);
    }
    pageViewModifier =
      pageViewModifier !== pageViewModifiers.NO_MOD ? pageViewModifier : getPageViewModifier(this.stores);
    if (!this.isContinuousViewMode && !isUndefined(pageViewModifier) && pageViewModifier !== pageViewModifiers.NO_MOD) {
      this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifier });
    }
    if (pageIndex >= 0) {
      this.stores.pagedViewState.setOpenAction({ pageIndex });
      this.stores.pagedViewState.goToPage(pageIndex + 1);
    }

    if (
      pageViewModifier === pageViewModifiers.FIT_PAGE &&
      shouldEnableFitOneFullPageMode(this.stores) &&
      prevDocViewMode === docViewModes.TWO_COLUMN
    ) {
      this.isViewWithScrolling = false;
      this.stores.documentStore.setViewMode(docViewModes.FIT_ONE_FULL_PAGE);
    }
  }

  @action
  setCustomUIForTwoColumnViewMode() {
    this.isTwoColumnViewMode = true;
  }

  @action
  toggleCoverMode(value) {
    const pageIndex = this.getCurrentPageIndex();
    if (isUndefined(value)) {
      this.isCoverPageMode = !this.isCoverPageMode;
    } else {
      this.isCoverPageMode = value;
    }
    if (pageIndex >= 0) {
      this.stores.pagedViewState.setOpenAction({ pageIndex });
      this.stores.pagedViewState.goToPage(pageIndex + 1);
    }
  }

  @action
  toggleViewWithScrolling(value) {
    if (isUndefined(value)) {
      this.isViewWithScrolling = !this.isViewWithScrolling;
    } else {
      this.isViewWithScrolling = value;
    }
  }

  @action
  setCustomUIForContinuousViewMode() {
    this.isContinuousViewMode = true;
    this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_WIDTH });
  }

  @action
  setCommonCustomUIForSinglePageAndPageByPageViewMode(pageIndex) {
    this.viewMode = docViewModes.SINGLE_PAGE;
    this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.NO_MOD });
    this.stores.documentStore.setAcroFormVisibility(false);

    // to show all annots in rendition i.e. comments and acroforms in case of slidemode
    this.stores.documentStore.setSelectivelyRenderAnnotsFlag(AnnotType.ALL);
    this.stores.overlayDropinStore.setShouldRenderPersonalCommentsDropin(false);
    this.customizeHudComponents({
      DOCK: false,
      FIT_PAGE: false,
      FIT_WIDTH: false
    });
    this.stores.lhpStore.setLHPVisibility(false);
    this.stores.appStore.enableZoomFeature(true);
    this.stores.zoom.disableInitialZoomLevelChange();
    this.stores.appUiState.setScrollbarSize(0);
    this.stores.searchStore.setShouldshowSearchInHud(false);
    if (pageIndex >= 0) this.stores.documentStore.setCurrPageIndexForSlideMode(pageIndex);
  }

  @action
  setCustomUIForFullScreen(pageIndex) {
    this.stores.appStore.setPanActive(true);
    this.stores.documentStore.setQuickToolsVisibility(false);
    this.stores.appUiState.setScrollbarSize(getScrollBarSize());
    this.setQuickToolsVisibility(false);
    this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_PAGE });
    // render all annots via t5 except acroform
    this.stores.documentStore.setSelectivelyRenderAnnotsFlag(Number(BigInt(AnnotType.ALL) ^ BigInt(AnnotType.WIDGET)));
    this.stores.documentStore.setOCGLayerVisibility(FILL_SIGN_OCG_NAME, true);
    this.stores.overlayDropinStore.setOverlayVisibility("COMMENT", OVERLAY_VISIBILITY.HIDDEN);
    this.stores.overlayDropinStore.setOverlayVisibility("fillsign", OVERLAY_VISIBILITY.HIDDEN);
    this.stores.zoom.setFixedMargins({
      left: 0,
      right: 0
    });
    this.stores.documentStore.pagesCacheWindow = MAX_PAGES_CACHE_WINDOW;
    if (pageIndex >= 0) this.stores.documentStore.setCurrPageIndexForFitOnePageMode(pageIndex);
  }

  @action
  setCustomUIForFitOneFullPageMode(pageIndex) {
    this.stores.appUiState.setScrollbarSize(getScrollBarSize());
    this.toggleViewWithScrolling(false);
    this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_PAGE });
    this.stores.zoom.setFixedMargins({
      left: this.showQuickTools ? PAGES_VIEW_MARGIN_LEFT : 0,
      right: this.showQuickTools ? PAGES_VIEW_MARGIN_RIGHT : 0
    });
    if (pageIndex >= 0) this.stores.documentStore.setCurrPageIndexForFitOnePageMode(pageIndex);
  }

  @action
  setCustomUIForSinglePageViewMode() {
    this.stores.hudStore.setIsDocked(true);
    this.stores.hudStore.setHudDisable(false);
    this.stores.pagedViewState.setShowScrubber(false);
    if (getShouldUseModernViewer(this.stores)) {
      this.customizeHudComponents({
        DOWNLOAD: true,
        PRINT: true
      });
      this.stores.searchStore.setShouldshowSearchInHud(true);
    }
    this.setRhpVisibility(false);
    this.setQuickToolsVisibility(false);
    this.stores.zoom.setFixedMargins({
      left: 0,
      right: 0
    });
  }

  @action
  setCustomUIForPageByPageViewMode() {
    this.isPageByPageViewMode = true;
    this.stores.hudStore.setHudDisable(true);
    this.stores.pagedViewState.setShowScrubber(true);
  }

  @action
  setCustomUIForInPlaceViewMode(pageIndex) {
    this.viewMode = docViewModes.IN_PLACE;
    this.customizeHudComponents({
      DOCK: false,
      FIT_PAGE: false,
      FIT_WIDTH: false,
      PAGE_NAVIGATION: true,
      DOWNLOAD: true,
      PRINT: true
    });
    this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_WIDTH });
    this.stores.hudStore.setIsDocked(false);
    this.stores.hudStore.setShouldAutoHide(false);
    this.stores.pagedViewState.setShowScrubber(false);
    this.stores.lhpStore.setLHPVisibility(false);
    this.stores.appStore.enableZoomFeature(false);
    this.stores.appStore.enableSearchFeature(true);
    this.stores.appUiState.setScrollbarSize(0);
    this.stores.searchStore.setShouldshowSearchInHud(true);
    if (pageIndex >= 0) {
      this.stores.pagedViewState.setOpenAction({ pageIndex });
      this.stores.pagedViewState.goToPage(pageIndex + 1);
    }
    this.setRhpVisibility(false);
    this.setQuickToolsVisibility(false);
    this.stores.zoom.setFixedMargins({
      left: "auto",
      right: "auto"
    });
  }

  getCurrentPageIndex() {
    let pageIndex = -1;
    if (this.viewMode === docViewModes.SINGLE_PAGE) {
      pageIndex = getPageIndexForSlideMode(this.stores);
    } else {
      pageIndex = getCurrentPageIndex(this.stores);
    }
    return pageIndex;
  }

  @action
  setViewMode(viewMode) {
    // Reset element handle so that new view mode's container can safely update the elementHandle
    this.stores.pagedViewState.resetPagesViewElementHandle();

    // get current page index before switching the view mode
    const pageIndex = this.getCurrentPageIndex();
    // trigger view mode change event. This is used by comment drop in to show comments by page
    overlayEvents.trigger("viewModeChange", viewMode);
    let prevDocViewMode = this.viewMode;
    if (this.isTwoColumnViewMode) {
      prevDocViewMode = docViewModes.TWO_COLUMN;
    }
    this.viewMode = viewMode;
    this.isContinuousViewMode = false;
    this.isTwoColumnViewMode = false;
    this.isPageByPageViewMode = false;
    this.stores.hudStore.resetExternalComponents(hudUiControlAlignments.RIGHT);
    this.stores.hudStore.resetExternalComponents(hudUiControlAlignments.LEFT);

    let pageViewModifier = pageViewModifiers.NO_MOD;
    // This check ensures the following mode should not break rendering. DocOpen check is used in
    // DocumentSelectors - shouldStartRendering.
    if (
      viewMode === docViewModes.NON_PDF ||
      viewMode === docViewModes.LIQUID_MODE_VIEW ||
      viewMode === docViewModes.NO_PREVIEW
    ) {
      this.stores.documentStore.setDocOpenActionsCompleted(true);
    }
    if (
      viewMode === docViewModes.TWO_COLUMN_FIT_WIDTH ||
      viewMode === docViewModes.TWO_COLUMN_FIT_PAGE ||
      viewMode === docViewModes.TWO_COLUMN
    ) {
      pageViewModifier =
        viewMode === docViewModes.TWO_COLUMN_FIT_PAGE ? pageViewModifiers.FIT_PAGE : pageViewModifiers.FIT_WIDTH;
      viewMode = docViewModes.TWO_COLUMN; // setting a common view mode as rest of handeling same ahead
    }
    // Set initial config for switching doc view modes
    if (
      viewMode === docViewModes.CLASSIC_VIEW ||
      viewMode === docViewModes.CONTINUOUS ||
      viewMode === docViewModes.TWO_COLUMN
    ) {
      if (viewMode === docViewModes.CONTINUOUS) {
        this.setCustomUIForContinuousViewMode();
      } else if (viewMode === docViewModes.TWO_COLUMN) {
        this.setCustomUIForTwoColumnViewMode();
      }

      this.setCommonCustomUIForClassicContinuousAndTwoColumnViewMode(pageIndex, pageViewModifier, prevDocViewMode);
    } else if (viewMode === docViewModes.SINGLE_PAGE || viewMode === docViewModes.PAGE_BY_PAGE) {
      this.setCommonCustomUIForSinglePageAndPageByPageViewMode(pageIndex);
      if (viewMode === docViewModes.SINGLE_PAGE) {
        this.setCustomUIForSinglePageViewMode();
      } else if (viewMode === docViewModes.PAGE_BY_PAGE) {
        this.setCustomUIForPageByPageViewMode();
      }
    } else if (viewMode === docViewModes.IN_PLACE) {
      this.setCustomUIForInPlaceViewMode(pageIndex);
    } else if (viewMode === docViewModes.FULL_SCREEN) {
      this.setCustomUIForFullScreen(pageIndex);
    } else if (viewMode === docViewModes.FIT_ONE_FULL_PAGE) {
      this.setCustomUIForFitOneFullPageMode(pageIndex);
    }
    if (getShowSearchInputUI(this.stores)) {
      this.stores.searchStore.setDefaultSearchQuery(getSearchManager(this.stores).getQueryString());
      getSearchManager(this.stores).closeSearchUI();
      this.stores.searchStore.setShowSearchInputUI(false);
    }
    if (this.viewMode === docViewModes.NO_PREVIEW) {
      this.setShouldShowPageLoader(false);
    }
  }

  setPageHandle(pageHandle, pageIndex) {
    if (this.isValidPageIndex(pageIndex)) {
      this.pages[pageIndex].elementHandle = pageHandle;
    }
  }

  setFontList(fontList, pageIndex) {
    if (this.isValidPageIndex(pageIndex)) {
      this.pages[pageIndex].fontList = fontList;
    }
  }

  @computed
  get maxWidth() {
    return this.pages.length && this.pages[this.maxWidthIndex] !== undefined
      ? this.pages[this.maxWidthIndex].origWidth
      : 0;
  }

  @computed
  get maxWidthIndex() {
    if (isFullScreenOrFitOnePageMode(this.stores)) {
      return getCurrentPageIndex(this.stores);
    }
    let maxWidth = 0;
    let maxWidthIndex = 0;
    if (this.pages.length) {
      for (let i = 0; i < this.pages.length; i++) {
        const pageOrigWidth = this.pages[i].origWidth;
        if (maxWidth < pageOrigWidth) {
          maxWidth = pageOrigWidth;
          maxWidthIndex = i;
        }
      }
    }
    return maxWidthIndex;
  }

  @computed
  get maxHeight() {
    return this.pages[this.maxHeightIndex] !== undefined ? this.pages[this.maxHeightIndex].origHeight : 0;
  }

  @computed
  get maxHeightIndex() {
    if (isFullScreenOrFitOnePageMode(this.stores)) {
      return getCurrentPageIndex(this.stores);
    }
    let maxHeight = 0;
    let maxHeightIndex = 0;
    if (this.pages.length) {
      for (let i = 0; i < this.pages.length; i++) {
        const pageOrigHeight = this.pages[i].origHeight;
        if (maxHeight < pageOrigHeight) {
          maxHeight = pageOrigHeight;
          maxHeightIndex = i;
        }
      }
    }
    return maxHeightIndex;
  }

  @computed
  get currMaxWidth() {
    if (getShouldUseModernViewer(this.stores)) {
      return this.pages[this.maxWidthIndex] !== undefined ? this.pages[this.maxWidthIndex].currPageWidth : 0;
    }

    let currMaxWidth = 0;
    if (this.pages.length) {
      for (let i = 0; i < this.pages.length; i++) {
        const currPageWidth = this.pages[i].currPageWidth;
        if (currMaxWidth < currPageWidth) {
          currMaxWidth = currPageWidth;
        }
      }
    }
    return currMaxWidth;
  }

  @computed
  get currMaxHeight() {
    if (getShouldUseModernViewer(this.stores)) {
      return this.pages[this.maxHeightIndex] !== undefined ? this.pages[this.maxHeightIndex].currPageHeight : 0;
    }

    let currMaxHeight = 0;
    if (this.pages.length) {
      for (let i = 0; i < this.pages.length; i++) {
        const currPageHeight = this.pages[i].currPageHeight;
        if (currMaxHeight < currPageHeight) {
          currMaxHeight = currPageHeight;
        }
      }
    }
    return currMaxHeight;
  }

  @action
  setShouldRecomputePageSizes(value) {
    if (isBoolean(value)) {
      this.shouldRecomputePageSizes = value;
    }
  }

  @action
  setUseReactVirtualized(value) {
    if (isBoolean(value)) {
      this.useReactVirtualized = value;
    }
  }

  @action
  setIsTilingEnabled(value) {
    if (isBoolean(value)) {
      this.isTilingEnabled = value;
    }
  }

  @action
  setDisplayToast(value) {
    this.displayToast = value;
  }

  @action
  setDisplayUnsupportedFeatureAlert(value) {
    this.displayUnsupportedFeatureAlert = value;
  }

  @action
  setDisplayOpenInDesktopOnUnsupportedNotification(value) {
    this.displayOpenInDesktopOnUnsupportedNotification = value;
  }

  @action
  setDisplayPermissionToast(value) {
    this.displayPermissionToast = value;
  }

  @action
  setViewDocumentProperties(value) {
    this.viewDocumentProperties = value;
  }

  @action.bound setShouldAutoHideToast(value) {
    this.shouldAutoHideToast = value;
  }

  @action
  setIsMouseOverToast(value) {
    this.isMouseOverToast = value;
  }

  @action
  loadItemsInView(itemsInView, getItemLoadStatus, loadItem, unloadItem, getItemZoomLevel, getCacheManager) {
    for (let itemIndex = 0; itemIndex < this.pages.length; itemIndex++) {
      if (
        (itemIndex < itemsInView.start || itemIndex > itemsInView.end) &&
        getItemLoadStatus(this.stores, itemIndex) === loadStatus.BEING_LOADED
      ) {
        unloadItem(itemIndex);
      }
    }
    for (let itemIndex = itemsInView.start; itemIndex <= itemsInView.end; itemIndex++) {
      loadItem(itemIndex, getItemZoomLevel(this.stores, itemIndex), itemsInView);
    }
    let cacheManager = getCacheManager();
    if (cacheManager) {
      cacheManager.cacheItems(itemsInView);
    }
  }

  @action
  loadPagesInView(pagesInView) {
    this.loadItemsInView(
      pagesInView,
      getPageLoadStatus,
      this.loadPage,
      this.unloadPage,
      getPageZoomLevel,
      getPagesCacheManager
    );
    for (let itemIndex = 0; itemIndex < this.pages.length; itemIndex++) {
      if (itemIndex < pagesInView.start || itemIndex > pagesInView.end) {
        this.unloadAcroformAppearance(itemIndex, item => item.status === loadStatus.BEING_LOADED);
      }
    }
    if (this.isValidPageRange(pagesInView.start, pagesInView.end)) {
      const overlayApis = getPageOverlayApis(this.stores, "acroform");
      if (overlayApis) {
        overlayApis.invalidateFieldsAppearance({
          pageRange: {
            pageStart: pagesInView.start,
            pageEnd: pagesInView.end
          }
        });
      }
    }
  }

  @action
  loadThumbnailsInView(thumbnailsInView) {
    this.loadItemsInView(
      thumbnailsInView,
      getThumbnailLoadStatus,
      this.loadThumbnail,
      this.unloadThumbnail,
      getThumbnailZoomLevelForAJS,
      getThumbnailsCacheManager
    );
  }

  setSelectivelyRenderAnnotsFlag(selectivelyRenderAnnotsFlag) {
    this.selectivelyRenderAnnotsFlag = BigInt(selectivelyRenderAnnotsFlag);
    if (getShouldEnableVoiceAnnotations(this.stores)) {
      this.selectivelyRenderAnnotsFlag ^= BigInt(AnnotType.SOUND);
    }
    this.selectivelyRenderAnnotsFlag = Number(this.selectivelyRenderAnnotsFlag);
  }

  isPageLoadedPromise(pageIndex) {
    return new Promise(resolve => {
      subscribeToSelector(
        this.stores,
        stores => getPageLoadStatus(stores, pageIndex),
        (value, reaction) => {
          if (value === loadStatus.LOADED) {
            reaction.dispose();
            resolve();
          }
        }
      );
    });
  }

  initialPageRenderedPromise() {
    return new Promise(resolve => {
      subscribeToSelector(this.stores, getIsInitialPageRendered, (value, reaction) => {
        if (value === true) {
          reaction.dispose();
          resolve(true);
        }
      });
    });
  }

  setTriggerDocumentViewUpdateEvent(value) {
    this.triggerDocumentViewUpdateEvent = value;
  }

  async refreshGeometries(startPageNum, endPageNum) {
    if (!this.isValidPageRange(startPageNum - 1, endPageNum - 1)) {
      return Promise.reject("Invalid Page Range");
    }

    const resp = await this.docApi.getPageRangeGeometry(
      startPageNum,
      endPageNum,
      FORCE_FETCH_PAGE_GEOMETRY_PRIORITY,
      true
    );
    const pagesGeometry = populatePagesGeometryData(resp.data);
    this.resetPagesGeometry(startPageNum - 1, endPageNum - 1, pagesGeometry);
    return Promise.resolve();
  }

  /**
   * Reloads a list of pages and their geometries.
   * Sorts and processes pages, refreshing geometries if required, and reloads valid pages in view.
   *
   * @param {Array} pages - List of page numbers to reload.
   * @param {Object} options - Optional settings (e.g., { refreshGeometry: boolean }).
   * @returns {Promise} Resolves when the operation is complete or rejects on invalid input.
   */
  async reloadPageListAndGeometries(pages, options = {}) {
    if (!Array.isArray(pages) || pages.length === 0) {
      return Promise.reject("Invalid pages array");
    }

    pages.sort((a, b) => a - b);

    if (options.refreshGeometry) {
      const geometryPromises = [];
      let currentRangeStart = pages[0];
      for (let i = 1; i < pages.length; i++) {
        if (pages[i] !== pages[i - 1] + 1) {
          geometryPromises.push(this.refreshGeometries(currentRangeStart, pages[i - 1]));
          currentRangeStart = pages[i];
        }
      }
      // Refresh the last range
      geometryPromises.push(this.refreshGeometries(currentRangeStart, pages[pages.length - 1]));
      await Promise.all(geometryPromises);
    }

    // reload Pages
    const pagesInViewObj = pagesInView(this.stores);

    pages.forEach(pageNum => {
      const pageIndex = pageNum - 1;

      if (this.isValidPageIndex(pageIndex)) {
        this.unloadPage(pageIndex);

        if (pageIndex >= pagesInViewObj.start && pageIndex <= pagesInViewObj.end) {
          this.loadPage(pageIndex, getPageZoomLevel(this.stores, pageIndex), pagesInViewObj);
        }
      }
    });

    return Promise.resolve();
  }

  /**
   * Reloads a range of pages and their geometries.
   * Fetches and resets geometry data for the specified range and reloads visible pages and thumbnails.
   *
   * @param {number} startPageNum - The starting page number of the range.
   * @param {number} endPageNum - The ending page number of the range.
   * @param {Object} options - Optional settings (e.g., { refreshGeometry: boolean }).
   * @returns {Promise} Resolves when the operation is complete or rejects on invalid page range.
   */
  async reloadPagesAndGeometries(startPageNum, endPageNum, options = {}) {
    if (!this.isValidPageRange(startPageNum - 1, endPageNum - 1)) {
      return Promise.reject("Invalid Page Range");
    }

    if (options.refreshGeometry) {
      await this.refreshGeometries(startPageNum, endPageNum);
    }

    const pagesInViewObj = pagesInView(this.stores);
    const thumbnailsInViewObj = thumbnailsInView(this.stores);

    this.reloadPages(pagesInViewObj);
    this.reloadThumbnails(thumbnailsInViewObj);
  }

  setOCGLayerVisibility(ocgName, isVisible) {
    const ocgIndex = this.hiddenOCGs.findIndex(element => element === ocgName);
    const isAlreadyHidden = ocgIndex !== -1;
    let isReloadRequired = false;
    if (isVisible && isAlreadyHidden) {
      this.hiddenOCGs.splice(ocgIndex, 1);
      isReloadRequired = true;
    } else if (!isVisible && !isAlreadyHidden) {
      this.hiddenOCGs.push(ocgName);
      isReloadRequired = true;
    }
    if (isReloadRequired) {
      return initialPageLoadedPromise(this.stores)
        .then(() => {
          let pagesInViewObj = pagesInView(this.stores);
          this.reloadPages(pagesInViewObj);
          let promiseArray = [];
          for (let itemIndex = pagesInViewObj.start; itemIndex <= pagesInViewObj.end; itemIndex++) {
            promiseArray.push(this.isPageLoadedPromise(itemIndex));
          }
          return Promise.all(promiseArray);
        })
        .catch(() => {});
    }
    return Promise.resolve();
  }

  setFreetextStatus(status) {
    this.enableFreetext = status;
  }

  @action
  setQuickToolsVisibility(visible) {
    if (isBoolean(visible)) {
      this.showQuickTools = visible;
    }
  }

  @action
  setStickyToolsVisibility(visible) {
    if (isBoolean(visible)) {
      this.showStickyTools = visible;
    }
  }

  @action
  setRhpVisibility(visible) {
    if (isBoolean(visible)) {
      this.showRhp = visible;
    }
  }

  @action
  setForceShowCommentingTools(value) {
    if (isBoolean(value)) {
      this.forceShowCommentingTools = value;
    }
  }

  @action
  registerSecondaryComponent(config) {
    if (config) {
      this.secondaryComponent = config;
    }
  }

  @action
  deRegisterSecondaryComponent() {
    this.secondaryFlyoutComponent = null;
    this.secondaryComponent = null;
  }

  @action
  setSecondaryFlyout(config) {
    if (this.secondaryComponent && config) {
      this.secondaryFlyoutComponent = config;
    }
  }

  @action
  resetSecondaryFlyout() {
    this.secondaryFlyoutComponent = null;
  }

  setPageMountStatus(pageIndex, value) {
    if (this.isValidPageIndex(pageIndex)) {
      this.pages[pageIndex].isMounted = value;
    }
  }

  clearLoadPageRequests(params) {
    this.docApi
      .clearMessageQueue(params)
      .then(() => {})
      .catch(() => {});
  }

  setPagesCacheWindow(zoomlevel) {
    this.pagesCacheWindow = Math.min(Math.ceil(CACHE_WINDOW_FOR_ZOOMLEVEL_1 / zoomlevel), MAX_PAGES_CACHE_WINDOW);
  }

  setLatestHitTestResponse(hitTestResponse) {
    this.latestHitTestResponse = hitTestResponse;
  }

  setPermittedOperations(permOperations) {
    this.permittedOperations = permOperations;
  }

  setShowUnsupportedFieldAlert(value) {
    this.showUnsupportedFieldAlert = value;
  }

  setTriggerUnsupportedFieldEvent(value) {
    this.triggerUnsupportedFieldEvent = value;
  }

  setUpdateObjectCount(value) {
    this.updateObjectCount = value;
  }

  @action
  setIsCropMode(value) {
    if (isBoolean(value)) {
      this.isCropMode = value;
    }
  }

  resetSecondaryComponent() {
    if (this.secondaryComponent && isFunction(this.secondaryComponent.resetSecondaryPanelState)) {
      this.secondaryComponent.resetSecondaryPanelState();
    }
  }

  setIsLinearizedWorkflow(isLinearizedWorkflow) {
    this.isLinearizedWorkflow = isLinearizedWorkflow;
  }

  @action.bound
  setPagesInDOM(pagesInDOM) {
    this.pagesInDOM = pagesInDOM;
  }

  setBaseUrl(baseURL) {
    this.baseURL = baseURL;
  }

  setUseProgressiveRendering(value = false) {
    this.useProgressiveRendering = value;
  }

  setProgressiveRenderInterval(value) {
    const numValue = Number(value);
    if (!Number.isNaN(numValue)) {
      this.progressiveRenderInterval = numValue;
    }
  }

  setProgressiveRenderMaxInterval(value) {
    const numValue = Number(value);
    if (!Number.isNaN(numValue)) {
      this.progressiveRenderMaxInterval = numValue;
    }
  }

  setRenderIntervalMultiplier(value) {
    const numValue = Number(value);
    if (!Number.isNaN(numValue)) {
      this.renderIntervalMultiplier = numValue;
    }
  }

  @action
  setLastOpenedPage(value) {
    this.lastOpenedPage = value;
  }

  @action
  setProgressivelyRenderedPageStatus(pageIndex, status = loadStatus.NOT_LOADED) {
    if (!this.isValidPageIndex(pageIndex)) {
      return;
    }
    if (!this.progressivelyRenderedPages[pageIndex]) {
      this.progressivelyRenderedPages[pageIndex] = {};
    }
    this.progressivelyRenderedPages[pageIndex].status = status;
  }

  @action
  unloadAllProgressivePages() {
    Object.keys(this.progressivelyRenderedPages).forEach(key => {
      this.setProgressivelyRenderedPageStatus(key, loadStatus.UNLOADED);
    });
  }

  setFileStorageType(value) {
    this.fileStorageType = value;
  }

  setFileShareType(value) {
    this.fileShareType = value;
  }

  setPerfMetadata(value) {
    this.perfMetadata = value;
  }

  setLogGetPageRenditionInfo(value) {
    this.logGetPageRenditionInfo = value;
  }

  updateScrollDirection(currentScrollTop) {
    if (this.lastScrollTop > currentScrollTop) {
      this.scrollDirection = ScrollDirection.BACKWARD;
    } else {
      this.scrollDirection = ScrollDirection.FORWARD;
    }
    this.lastScrollTop = currentScrollTop;
  }

  updateCacheBurstInfo(key, value) {
    switch (key) {
      case cacheBurstLogKeys.DIFF_CONS_BURST:
        if (this.cacheBurstInfo.diffBwConsecutiveCacheBurst === 0 && value > 0) {
          this.cacheBurstInfo.diffBwConsecutiveCacheBurst = value;
        } else {
          this.cacheBurstInfo.diffBwConsecutiveCacheBurst = Math.min(
            this.cacheBurstInfo.diffBwConsecutiveCacheBurst,
            value
          );
        }
        if (this.logEarlyCacheBurstInfo) {
          logCacheBurstInfo({
            ...getCacheBurstInfoForLogging(this.stores),
            earlyBurstInfo: true,
            cacheBurstCount: 2
          });
          this.logEarlyCacheBurstInfo = false;
        }
        break;
      case cacheBurstLogKeys.TIME_CACHE_FIX:
        this.cacheBurstInfo.cacheBurstCount++;
        if (this.cacheBurstInfo.numPagesCachedAtCacheBurst === 0) {
          this.cacheBurstInfo.numPagesCachedAtCacheBurst = getNumLoadedCachedPages(this.stores);
        } else {
          this.cacheBurstInfo.numPagesCachedAtCacheBurst = Math.min(
            getNumLoadedCachedPages(this.stores),
            this.cacheBurstInfo.numPagesCachedAtCacheBurst
          );
        }
        this.cacheBurstInfo.timeTakenInCacheFix = Math.max(this.cacheBurstInfo.timeTakenInCacheFix, value);
        break;
    }
  }

  setInitialPageLoadTime() {
    this.initialPageLoadTime = Date.now();
  }

  setIsFilteredDocOpenActionsPresent(value) {
    this.isFilteredDocOpenActionsPresent = value;
  }

  setHasFASFields(value = false) {
    this.hasFASFields = value;
  }

  setIsImageCurrentSelection(isImageCurrentSelection = false) {
    this.isImageCurrentSelection = isImageCurrentSelection;
  }
}

export { DocumentStore };
